import {
  CARE_AMOUNTS,
  FAMILY_HELP_OPTIONS,
  type CareAdjustments,
  type CareAmount,
  type FamilyHelp,
} from '@shared/consumerPlan';
import { CARE_AMOUNT_COPY, FAMILY_HELP_COPY } from '../lib/copy';
import { hoursPerWeek } from '../lib/format';
import { ChoiceList } from './choice-list';

interface CareAdjustmentChoicesProps {
  adjustments: CareAdjustments;
  careHoursByAmount: Record<CareAmount, number>;
  familyHoursByHelp: Record<FamilyHelp, number>;
  onCareAmountChange: (careAmount: CareAmount) => void;
  onFamilyHelpChange: (familyHelp: FamilyHelp) => void;
}

/** The two optional overrides: how much care to plan for, and how much of it family gives. */
export function CareAdjustmentChoices({
  adjustments,
  careHoursByAmount,
  familyHoursByHelp,
  onCareAmountChange,
  onFamilyHelpChange,
}: CareAdjustmentChoicesProps) {
  return (
    <div className="flex flex-col gap-6 text-gray-950">
      <p>Optional. If you’re not sure, keep our estimate and continue.</p>
      <fieldset className="flex flex-col gap-3">
        <legend className="mb-1 flex flex-col gap-1">
          <span className="font-semibold">How much care should we plan for?</span>
          <span className="text-muted-foreground">Adjust the amount of help you might need.</span>
        </legend>
        <ChoiceList
          label="Amount of care"
          value={adjustments.careAmount}
          onChange={onCareAmountChange}
          className="sm:grid-cols-3"
          choices={CARE_AMOUNTS.map(careAmount => ({
            value: careAmount,
            title: CARE_AMOUNT_COPY[careAmount],
            description: hoursPerWeek(careHoursByAmount[careAmount]),
          }))}
        />
      </fieldset>
      <fieldset className="flex flex-col gap-3">
        <legend className="mb-1 flex flex-col gap-1">
          <span className="font-semibold">How much help would family provide?</span>
          <span className="text-muted-foreground">
            Average hours a week across your spouse, children and other family, combined.
          </span>
        </legend>
        <ChoiceList
          label="Family involvement"
          value={adjustments.familyHelp}
          onChange={onFamilyHelpChange}
          className="sm:grid-cols-3"
          choices={FAMILY_HELP_OPTIONS.map(familyHelp => ({
            value: familyHelp,
            title: FAMILY_HELP_COPY[familyHelp],
            description: hoursPerWeek(familyHoursByHelp[familyHelp]),
          }))}
        />
      </fieldset>
    </div>
  );
}
